import { useMemo } from "react";
import { Select } from "@/components";
import type { Uuid } from "@/api/types";
import { useLocations } from "../api";

const NONE = "none";

/** Choose a location by code, with its path underneath ("A-01" / "Studio / Rack A"). */
export function LocationSelect({
  label = "Location",
  value,
  onValueChange,
  allowNone = true,
  noneLabel = "No location",
  size = "md",
  optional,
  description,
  error,
  name,
  hideLabel,
}: {
  label?: string;
  value: Uuid | null;
  onValueChange: (value: Uuid | null) => void;
  allowNone?: boolean;
  noneLabel?: string;
  size?: "sm" | "md";
  optional?: boolean;
  description?: string;
  error?: string | null;
  name?: string;
  hideLabel?: boolean;
}) {
  const locations = useLocations();
  const options = useMemo(
    () => [
      ...(allowNone ? [{ value: NONE, label: noneLabel }] : []),
      ...(locations.data ?? []).map((location) => ({
        value: location.id,
        label: `${location.code} ${location.name}`,
        detail: location.depth > 0 ? location.path : undefined,
      })),
    ],
    [locations.data, allowNone, noneLabel],
  );
  return (
    <Select
      label={label}
      hideLabel={hideLabel}
      name={name}
      size={size}
      options={options}
      value={value ?? (allowNone ? NONE : null)}
      onValueChange={(next) => onValueChange(!next || next === NONE ? null : next)}
      placeholder={locations.isPending ? "Loading locations…" : "Choose a location"}
      optional={optional}
      description={
        description ??
        (locations.isSuccess && locations.data.length === 0 ? "Add locations on the Locations page." : undefined)
      }
      error={error}
    />
  );
}
